'use client'

import { XIcon } from 'lucide-react'
import { Suspense } from 'react'

import { SelfAuthForm } from '@/components/auth/self-auth-form'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * In-page sign-in / sign-up for any closed-loop tool page. Wraps the shared
 * SelfAuthForm in a modal so the visitor authenticates without navigating away
 * to /auth — leaving the page is where the old `/sign-up` CTA lost them.
 *
 * SelfAuthForm reads `redirect_url` from the query string and returns to it on
 * success; each studio sets that param to the current path before opening, so
 * a successful login lands the visitor right back here, signed in, with their
 * inputs still in the form.
 *
 * DARK, LIKE THE PAGE UNDER IT. A default (light) dialog over the near-black
 * tool ground read as a different product interrupting the one the visitor was
 * using. The ground is reproduced with `marketing-theme` on the portaled
 * content and `dark` on the card inside it: that pair — and only that pair, as
 * a descendant, which is why they cannot both sit on one element — hits the
 * `.marketing-theme .dark` token block in globals.css, so the form's
 * `bg-background` / `text-foreground` / `--ring` utilities resolve to the warm
 * ink and Soku orange instead of `.dark`'s chromaless greys and near-white
 * primary. No `dark:` variants needed anywhere in the form.
 *
 * ONE HEADING, NOT TWO. The tool-specific copy is passed DOWN into the form
 * rather than rendered above it: the form draws its own title block, so a
 * visible DialogHeader here stacked "Sign up to generate" over "Your AI
 * Marketer that Never Sleeps" — two titles and two descriptions, with the
 * relevant one rendered smaller than the generic one. The DialogTitle and
 * DialogDescription remain for screen readers, which Radix requires and which
 * would otherwise warn.
 */
export function ToolAuthDialog({
  open,
  onOpenChange,
  title = 'Sign up to generate',
  description,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  description: string
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // The card below owns the surface, so the portaled content is reduced
        // to a positioning shell — otherwise its own `bg-background` (light,
        // resolved from `marketing-theme` without `dark`) framed the card.
        showCloseButton={false}
        className="marketing-theme w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] border-0 bg-transparent p-0 ring-0 sm:w-full sm:max-w-[26rem]"
      >
        <div className="dark bg-background text-foreground relative max-h-[90dvh] overflow-y-auto rounded-2xl border border-white/10 p-6 sm:p-7">
          <DialogHeader className="sr-only">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <DialogClose asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="absolute top-4 right-4 text-white/40 hover:text-white"
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </Button>
          </DialogClose>
          <Suspense fallback={null}>
            <SelfAuthForm layout="dialog" heading={{ title, description }} />
          </Suspense>
        </div>
      </DialogContent>
    </Dialog>
  )
}
